import React, { useState, useEffect } from 'react';
import PlayerService from '../../services/PlayerService';
import StatisticsDetailModal from './StatisticsDetailModal';

const formatNumber = (num) => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(2)}M`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}k`;
  }
  return num.toString();
};

const getProgressColor = (current, total) => {
  // Calculate percentage
  const percentage = Math.min((current / total) * 100, 100);
  
  // Base green color: rgb(51, 255, 51)
  // Darker green: rgb(20, 100, 20)
  
  // Calculate color components
  const r = Math.round(20 + (percentage / 100) * (51 - 20));
  const g = Math.round(100 + (percentage / 100) * (255 - 100));
  const b = Math.round(20 + (percentage / 100) * (51 - 20));
  
  return `rgb(${r}, ${g}, ${b})`;
};

const renderCheckmark = () => (
  <div style={{
    position: 'absolute',
    top: '6px',
    right: '8px',
    color: '#33ff33',
    fontSize: '16px',
    textShadow: '0 0 5px #33ff33',
    fontFamily: 'IBM BIOS, monospace',
    animation: 'pulse 2s infinite ease-in-out',
    zIndex: 2
  }}>
    ✓
  </div>
);

const Statistics = ({ onClose }) => {
  const [stats, setStats] = useState({
    totalQubits: 0,
    totalPackets: 0,
    omega3Players: 0,
    totalPlayers: 0,
    neuralDriftPercent: 0,
    loading: true
  });
  const [detailModal, setDetailModal] = useState(null); // 'qubits' or 'rank6' or null
  const [playerData, setPlayerData] = useState([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const players = await PlayerService.getAllPlayers();
        
        const totalQubits = players.reduce((sum, player) => sum + (player.qubits || 0), 0);
        const totalPackets = players.reduce((sum, player) => sum + (player.packets || 0), 0);
        const omega3Players = players.filter(player => player.omegaClearance === 3 || player.omegaClearance === '3').length;
        const totalPlayers = players.length;

        // Calculate Neural Drift (total anchor strength percentage)
        // Each player can have 0-75 anchorStrength, each 1 point = 1.33%
        // Total network drift = sum of all players' individual drift percentages / total players
        const totalAnchorStrength = players.reduce((sum, player) => sum + (player.anchorStrength || 0), 0);
        const maxPossibleAnchor = totalPlayers * 75;
        const neuralDriftPercent = maxPossibleAnchor > 0 ? parseFloat(((totalAnchorStrength / maxPossibleAnchor) * 100).toFixed(2)) : 0;

        // Store player data for pie charts
        setPlayerData(players.map(player => ({
          username: player.discordUsername || 'Unknown',
          qubits: player.qubits || 0,
          rank: player.rank || '0',
          omegaClearance: player.omegaClearance || null,
          anchorStrength: player.anchorStrength || 0
        })));

        setStats({
          totalQubits,
          totalPackets,
          omega3Players,
          totalPlayers,
          neuralDriftPercent,
          loading: false
        });
      } catch (error) {
        console.error('Error fetching statistics:', error);
        setStats(prev => ({ ...prev, loading: false }));
      }
    };

    fetchStats();
  }, []);

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        zIndex: 10000,
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        cursor: 'pointer'
      }}>
      <div style={{
        background: 'rgba(0, 0, 0, 0.95)',
        border: '2px solid #33ff33',
        borderRadius: '6px',
        padding: '20px',
        maxWidth: '420px',
        width: '90%',
        color: '#33ff33',
        boxShadow: '0 0 30px rgba(51, 255, 51, 0.3)',
        position: 'relative',
        backgroundImage: `
          linear-gradient(0deg, rgba(51, 255, 51, 0.05) 1px, transparent 1px),
          linear-gradient(90deg, rgba(51, 255, 51, 0.05) 1px, transparent 1px)
        `,
        backgroundSize: '15px 15px',
        animation: 'pulse 2s infinite ease-in-out'
      }}>
        <style>
          {`
            @keyframes pulse {
              0% { box-shadow: 0 0 30px rgba(51, 255, 51, 0.3); }
              50% { box-shadow: 0 0 40px rgba(51, 255, 51, 0.4); }
              100% { box-shadow: 0 0 30px rgba(51, 255, 51, 0.3); }
            }
            @keyframes dataFlicker {
              0% { opacity: 1; }
              95% { opacity: 1; }
              96% { opacity: 0.8; }
              97% { opacity: 1; }
              98% { opacity: 0.9; }
              100% { opacity: 1; }
            }
            @keyframes borderGlow {
              0% { border-color: #33ff33; }
              50% { border-color: #66ff66; }
              100% { border-color: #33ff33; }
            }
            @keyframes borderGlowWarning {
              0% { border-color: #ffcc00; }
              50% { border-color: #ffdd44; }
              100% { border-color: #ffcc00; }
            }
            @keyframes scanlineComplete {
              0% { transform: translateY(-100%); }
              100% { transform: translateY(100%); }
            }
            .stat-box {
              transition: all 0.3s ease;
            }
            .stat-box:hover {
              transform: scale(1.02);
              box-shadow: 0 0 20px rgba(51, 255, 51, 0.4);
              background: rgba(51, 255, 51, 0.1) !important;
            }
          `}
        </style>
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            background: 'none',
            border: 'none',
            color: '#33ff33',
            fontSize: '16px',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          ×
        </button>

        <h2 style={{
          textAlign: 'center',
          marginBottom: '20px',
          fontSize: '18px',
          textShadow: '0 0 10px #33ff33',
          fontFamily: 'IBM BIOS, monospace',
          letterSpacing: '2px',
          position: 'relative',
          animation: 'dataFlicker 5s infinite'
        }}>
          <div style={{
            position: 'absolute',
            bottom: '-15px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '80%',
            height: '1px',
            background: 'linear-gradient(90deg, transparent, #33ff33, transparent)',
            animation: 'dataFlicker 3s infinite'
          }} />
          <div>NETWORK STATISTICS</div>
          <div style={{
            fontSize: '11px',
            marginTop: '3px',
            opacity: 0.8,
            letterSpacing: '1px'
          }}>CHAPTER 2</div>
        </h2>

        {stats.loading ? (
          <div style={{
            textAlign: 'center',
            padding: '25px',
            fontSize: '12px',
            fontFamily: 'IBM BIOS, monospace',
            letterSpacing: '1px',
            animation: 'dataFlicker 1s infinite',
            textShadow: '0 0 8px #33ff33'
          }}>
            ANALYZING NETWORK DATA...
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div
              className="stat-box"
              onClick={() => {
                setDetailModal('qubits');
              }}
              style={{
                padding: '12px',
                border: '1px solid rgba(51, 255, 51, 0.5)',
                borderRadius: '6px',
                background: 'rgba(51, 255, 51, 0.05)',
                position: 'relative',
                overflow: 'hidden',
                animation: 'borderGlow 2s infinite ease-in-out',
                cursor: 'pointer'
              }}>
              {stats.totalQubits >= 5000000 && renderCheckmark()}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '1px',
                background: 'linear-gradient(90deg, transparent, #33ff33, transparent)',
                animation: 'dataFlicker 3s infinite'
              }} />
              <div style={{
                fontSize: '11px',
                marginBottom: '4px',
                opacity: 0.8,
                letterSpacing: '1px',
                fontFamily: 'IBM BIOS, monospace'
              }}>TOTAL QUBITS HARVESTED</div>
              <div style={{
                fontSize: '20px',
                fontFamily: 'IBM BIOS, monospace',
                letterSpacing: '1px',
                animation: 'dataFlicker 4s infinite'
              }}>
                {stats.totalQubits >= 5000000 ? (
                  <span style={{
                    color: '#33ff33',
                    textShadow: '0 0 8px #33ff33'
                  }}>
                    {formatNumber(stats.totalQubits)}
                  </span>
                ) : (
                  <>
                    <span style={{
                      color: getProgressColor(stats.totalQubits, 5000000),
                      textShadow: `0 0 8px ${getProgressColor(stats.totalQubits, 5000000)}`
                    }}>
                      {formatNumber(stats.totalQubits)}
                    </span>
                    <span style={{
                      color: '#33ff33',
                      textShadow: '0 0 8px #33ff33'
                    }}>/5M</span>
                  </>
                )}
              </div>
            </div>

            <div
              className="stat-box"
              onClick={() => {
                setDetailModal('sentients');
              }}
              style={{
                padding: '12px',
                border: '1px solid rgba(51, 255, 51, 0.5)',
                borderRadius: '6px',
                background: 'rgba(51, 255, 51, 0.05)',
                position: 'relative',
                overflow: 'hidden',
                animation: 'borderGlow 2s infinite ease-in-out',
                cursor: 'pointer'
              }}>
              {stats.totalPlayers >= 200 && renderCheckmark()}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '1px',
                background: 'linear-gradient(90deg, transparent, #33ff33, transparent)',
                animation: 'dataFlicker 3s infinite'
              }} />
              <div style={{
                fontSize: '11px',
                marginBottom: '4px',
                opacity: 0.8,
                letterSpacing: '1px',
                fontFamily: 'IBM BIOS, monospace'
              }}>TOTAL SENTIENTS</div>
              <div style={{
                fontSize: '20px',
                fontFamily: 'IBM BIOS, monospace',
                letterSpacing: '1px',
                animation: 'dataFlicker 4s infinite'
              }}>
                <span style={{
                  color: getProgressColor(stats.totalPlayers, 200),
                  textShadow: `0 0 8px ${getProgressColor(stats.totalPlayers, 200)}`
                }}>
                  {stats.totalPlayers}
                </span>
                <span style={{
                  color: '#33ff33',
                  textShadow: '0 0 8px #33ff33'
                }}>/200</span>
              </div>
            </div>

            <div
              className="stat-box"
              onClick={() => {
                setDetailModal('omega3');
              }}
              style={{
                padding: '12px',
                border: '1px solid rgba(51, 255, 51, 0.5)',
                borderRadius: '6px',
                background: 'rgba(51, 255, 51, 0.05)',
                position: 'relative',
                overflow: 'hidden',
                animation: 'borderGlow 2s infinite ease-in-out',
                cursor: 'pointer'
              }}>
              {stats.omega3Players >= 20 && renderCheckmark()}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '1px',
                background: 'linear-gradient(90deg, transparent, #33ff33, transparent)',
                animation: 'dataFlicker 3s infinite'
              }} />
              <div style={{
                fontSize: '11px',
                marginBottom: '4px',
                opacity: 0.8,
                letterSpacing: '1px',
                fontFamily: 'IBM BIOS, monospace'
              }}>OMEGA-3 CLEARANCE</div>
              <div style={{
                fontSize: '20px',
                fontFamily: 'IBM BIOS, monospace',
                letterSpacing: '1px',
                animation: 'dataFlicker 4s infinite'
              }}>
                <span style={{
                  color: getProgressColor(stats.omega3Players, 20),
                  textShadow: `0 0 8px ${getProgressColor(stats.omega3Players, 20)}`
                }}>
                  {stats.omega3Players}
                </span>
                <span style={{
                  color: '#33ff33',
                  textShadow: '0 0 8px #33ff33'
                }}>/20</span>
              </div>
            </div>

            <div
              className="stat-box"
              onClick={() => {
                setDetailModal('neuralDrift');
              }}
              style={{
                padding: '12px',
                border: '1px solid rgba(51, 255, 51, 0.5)',
                borderRadius: '6px',
                background: 'rgba(51, 255, 51, 0.05)',
                position: 'relative',
                overflow: 'hidden',
                animation: 'borderGlow 2s infinite ease-in-out',
                cursor: 'pointer'
              }}>
              {stats.neuralDriftPercent >= 100 && renderCheckmark()}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '1px',
                background: 'linear-gradient(90deg, transparent, #33ff33, transparent)',
                animation: 'dataFlicker 3s infinite'
              }} />
              <div style={{
                fontSize: '11px',
                marginBottom: '4px',
                opacity: 0.8,
                letterSpacing: '1px',
                fontFamily: 'IBM BIOS, monospace'
              }}>NEURAL DRIFT</div>
              <div style={{
                fontSize: '20px',
                fontFamily: 'IBM BIOS, monospace',
                letterSpacing: '1px',
                animation: 'dataFlicker 4s infinite'
              }}>
                <span style={{
                  color: getProgressColor(stats.neuralDriftPercent, 100),
                  textShadow: `0 0 8px ${getProgressColor(stats.neuralDriftPercent, 100)}`
                }}>
                  {stats.neuralDriftPercent}%
                </span>
              </div>
              {/* Progress bar */}
              <div style={{
                marginTop: '8px',
                height: '6px',
                background: 'rgba(51, 255, 51, 0.1)',
                borderRadius: '3px',
                overflow: 'hidden',
                border: '1px solid rgba(51, 255, 51, 0.3)'
              }}>
                <div style={{
                  width: `${stats.neuralDriftPercent}%`,
                  height: '100%',
                  background: `linear-gradient(90deg, ${getProgressColor(stats.neuralDriftPercent, 100)}, #33ff33)`,
                  boxShadow: `0 0 8px ${getProgressColor(stats.neuralDriftPercent, 100)}`,
                  transition: 'width 0.5s ease-out'
                }} />
              </div>
            </div>

            {/* Completion Message */}
            {stats.totalQubits >= 5000000 && stats.totalPlayers >= 200 && stats.omega3Players >= 20 && (
              <div style={{
                marginTop: '10px',
                padding: '10px',
                border: '1px solid #ffcc00',
                borderRadius: '6px',
                background: 'rgba(255, 204, 0, 0.08)',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                animation: 'borderGlowWarning 1.5s infinite ease-in-out'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(transparent 30%, rgba(255, 204, 0, 0.03))',
                  animation: 'scanlineComplete 3s linear infinite',
                  pointerEvents: 'none'
                }} />
                <div style={{
                  fontFamily: 'IBM BIOS, monospace',
                  color: '#ffcc00',
                  textShadow: '0 0 6px #ffcc00',
                  letterSpacing: '1px',
                  opacity: 0.7,
                  animation: 'dataFlicker 3s infinite',
                  position: 'relative',
                  zIndex: 1,
                  lineHeight: '1.4'
                }}>
                  <div style={{ fontSize: '10px' }}>
                    <span style={{ fontSize: '11px', marginRight: '4px' }}>⚠</span>
                    NETWORK CAPACITY WARNING
                    <span style={{ fontSize: '11px', marginLeft: '4px' }}>⚠</span>
                  </div>
                  <div style={{ fontSize: '9px', marginTop: '2px' }}>
                    standby for transmissions
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Detail Modals */}
      {detailModal === 'qubits' && playerData.length > 0 && (
        <StatisticsDetailModal
          data={playerData
            .filter(player => player.qubits > 0)
            .map(player => ({
              username: player.username || 'Unknown User',
              value: player.qubits
            }))
            .sort((a, b) => b.value - a.value)} // Sort by qubit count descending
          type="qubits"
          onClose={() => setDetailModal(null)}
        />
      )}
      {detailModal === 'omega3' && playerData.length > 0 && (
        <StatisticsDetailModal
          data={playerData
            .filter(player => player.omegaClearance === 3 || player.omegaClearance === '3')
            .map(player => ({
              username: player.username || 'Unknown User',
              value: 1, // Equal segments for omega-3 players
              qubits: player.qubits || 0 // Keep qubit count for sorting
            }))
            .sort((a, b) => b.qubits - a.qubits)} // Sort by qubit count descending
          type="omega3"
          onClose={() => setDetailModal(null)}
        />
      )}
      {detailModal === 'sentients' && playerData.length > 0 && (
        <StatisticsDetailModal
          data={playerData.map(player => ({
            username: player.username,
            value: 1 // Each sentient counts as 1
          }))}
          type="sentients"
          onClose={() => setDetailModal(null)}
        />
      )}
      {detailModal === 'neuralDrift' && playerData.length > 0 && (
        <StatisticsDetailModal
          data={playerData
            .map(player => ({
              username: player.username || 'Unknown User',
              value: player.anchorStrength || 0
            }))
            .sort((a, b) => b.value - a.value)}
          type="neuralDrift"
          onClose={() => setDetailModal(null)}
        />
      )}
    </div>
  );
};

export default Statistics;